import React, { useState, useEffect, useRef } from 'react';
import RefreshIcon from '../icons/RefreshIcon';
import ExportIcon from '../icons/ExportIcon';
import ExportModal from '../modals/ExportModal';
import ReportDialogDetails from '../modals/ReportDialogDetails';
import ChartVisualization from '../charts/ChartVisualization';
import TableVisualization from '../TableVisualization';
import KPICard from '../cards/KPICard';
import OverviewSummary from '../OverviewSummary';
import OverviewPositioning from '../OverviewPositioning';
import OverviewRecommendations from '../OverviewRecommendations';
import SWOTAnalysis from '../SWOTAnalysis';
import NextSteps from '../NextSteps';
import Toast from '../ui/Toast';
import { Project } from '../../types/project';
import { StreamingQueryResponse } from '../../services/streamingApi';
import './WorkspaceContent.css';
import TabSummary from '../TabSummary';

interface WorkspaceContentProps {
  project: Project;
  streamResult?: StreamingQueryResponse;
  isStreaming?: boolean;
  isFollowUpQuery?: boolean;
  onSyncProject?: () => void;
  isSyncing?: boolean;
  syncProgress?: number;
  hasOverlay?: boolean;
}

const WorkspaceContent: React.FC<WorkspaceContentProps> = ({ project, streamResult, isStreaming = false, isFollowUpQuery = false, onSyncProject, isSyncing = false, syncProgress = 0, hasOverlay = false }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const contentBodyRef = useRef<HTMLDivElement>(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');



  // Typing effect for the streaming text
  useEffect(() => {
    // Determine the text to display based on state
    let text = '';
    if (isSyncing) {
      text = 'Syncing Dashboard...';
    } else if (streamResult?.status === 'pending_approval' && streamResult?.executionPlan) {
      text = 'Waiting Approval...';
    } else {
      text = 'Conducting Analysis...';
    }

    if ((!isStreaming && !isSyncing) || isFollowUpQuery) {
      setTypedText('');
      return;
    }

    let index = 0;
    let typeInterval: NodeJS.Timeout;
    let retypeTimeout: NodeJS.Timeout;

    const startTyping = () => {
      index = 0;
      setTypedText('');

      typeInterval = setInterval(() => {
        if (index < text.length) {
          setTypedText(text.substring(0, index + 1));
          index++;
        } else {
          clearInterval(typeInterval);
          // Wait 2 seconds, then retype
          retypeTimeout = setTimeout(() => {
            if (isStreaming || isSyncing) startTyping();
          }, 2000);
        }
      }, 60); // 60ms per character for slower typing
    };

    startTyping();

    return () => {
      clearInterval(typeInterval);
      clearTimeout(retypeTimeout);
    };
  }, [isStreaming, isSyncing, isFollowUpQuery, streamResult?.status, streamResult?.executionPlan]);

  const handleSyncProject = () => {
    if (onSyncProject) {
      onSyncProject();
    }
  };

  // Show/hide toast based on syncing state
  useEffect(() => {
    if (isSyncing) {
      setToastMessage('Syncing project data...');
      setToastType('info');
      setToastVisible(true);
    } else if (toastMessage === 'Syncing project data...') {
      // Hide the toast when syncing completes
      setToastVisible(false);
    }
  }, [isSyncing]);

  const handleExport = () => {
    setIsExportDialogOpen(true);
  };

  // Get the last sync time - use streamResult.endTime if available, otherwise fall back to project.updated_at
  const getLastSyncTime = () => {
    if (streamResult?.endTime) {
      return new Date(streamResult.endTime);
    }
    return new Date(project.updated_at);
  };

  const handleExportDialogClose = () => {
    setIsExportDialogOpen(false);
  };

  const handleExportConfirm = async (options: any, error?: string) => {

    if (error) {
      // Show error toast
      setToastMessage(error);
      setToastType('error');
      setToastVisible(true);
    } else if (options.format === 'pdf') {
      // Show success toast for PDF
      setToastMessage('PDF report exported successfully');
      setToastType('success');
      setToastVisible(true);
    } else if (options.format === 'powerpoint') {
      // PowerPoint not yet implemented
      setToastMessage('PowerPoint report exported successfully');
      setToastType('success');
      setToastVisible(true);
    }
  };

  // Helper function to get areas with fallback
  const getAreas = () => {
    // Primary: Check direct areas property (new structure)
    // Fallback: Check structured_response.areas (legacy structure)
    return streamResult?.structured_response?.areas || streamResult?.areas || {};
  };

  // Generate dynamic tabs based on structured response areas
  const getDynamicTabs = () => {
    let tabsList = [];

    // Always add Overview tab first if it's not already present
    const overviewTab = {
      id: 'overview',
      label: 'Overview',
      originalKey: 'Overview'
    };

    const areas = getAreas();
    if (areas && Object.keys(areas).length > 0) {
      const areaNames = Object.keys(areas);

      // Check if Overview already exists in the areas
      const hasOverview = areaNames.some(name =>
        name.toLowerCase() === 'overview' ||
        name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-') === 'overview'
      );

      // Add Overview tab if it doesn't exist
      if (!hasOverview) {
        tabsList.push(overviewTab);
      }

      const tabLabel = (areaName: string): string => {

        const area = areaName.replace(/_/g, ' ');

        if (project?.initial_query?.toLowerCase().includes('strategic positioning') ||
          project?.initial_query?.toLowerCase().includes('structured positioning analysis')) {

          if (area.toLowerCase() === 'trends') {
            return 'Competitive Landscape';
          } else if (area.toLowerCase() === 'competitor') {
            return 'Finance';
          } else if (area.toLowerCase() === 'customer insights') {
            return 'Regulatory';
          }
        }

        return area;
      };

      // Add all area tabs
      const areaTabs = areaNames.map((areaName, index) => ({
        id: areaName.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-'),
        label: tabLabel(areaName),
        originalKey: areaName // Store the original key for data lookup
      }));

      tabsList.push(...areaTabs);
      if (project?.initial_query?.toLowerCase().includes('strategic positioning') ||
        project?.initial_query?.toLowerCase().includes('structured positioning analysis')) {
        tabsList = tabsList.filter(tab => tab.id !== 'technology'); // Exclude Technology if already added
      }
    } else {
      // If no areas at all, just add Overview tab
      tabsList.push(overviewTab);
    }

    return tabsList;
  };

  const tabs = getDynamicTabs();


  // Helper function to render area content dynamically
  const renderAreaContent = (areaName: string, areaData: any) => {
    if (!areaData) return <p>No data available for this area.</p>;

    let tabTitle = 'Performance Overview';
    switch (areaName) {
      case 'Market':
        tabTitle = 'Market Outlook';
        break;
      case 'Trends':
        tabTitle = 'Trend Analysis';
        break;
      case 'Competitor':
        tabTitle = 'Competitive Landscape';
        break;
      case 'Customer Insights':
      case 'Customer':
        tabTitle = 'Customer Segmentation ';
        break;
      case 'Technology':
        tabTitle = 'Technology Landscape';
        break;

      default:
        break;
    }

    return (
      <div className="area-content">
        {/* Render content if available */}
        {areaData.content && (
          <TabSummary
            content={areaData.content}
            title={areaName}
            keyInsights={areaData.key_points}
            sources={areaData.sources}
          />
        )}

        {/* Show Strategic Positioning with report cards */}
        <OverviewPositioning
          charts={areaData?.charts}
          title={tabTitle}
          content=''
          onViewInsight={handleViewInsight}
        />

        {/* Show Strategic Recommendations */}
        <OverviewRecommendations
          recommendations={areaData?.recommendations}
        />

      </div>
    );
  };

  const handleViewInsight = (reportData: any) => {
    setSelectedReport(reportData);
    setIsReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setIsReportDialogOpen(false);
    setSelectedReport(null);
  };

  const renderTabContent = () => {
    if (activeTab === 'overview') {
      const areas = getAreas();
      const overviewArea = areas?.Overview;

      return (
        <div className="tab-content">
          {streamResult ? (
            <div className="analysis-results">
              {/* Show OverviewSummary with executive summary and key insights */}
              <OverviewSummary
                executiveSummary={overviewArea?.summary}
                keyInsights={overviewArea?.key_points}
                sources={overviewArea?.sources}
              />

              {/* Show Strategic Positioning with report cards */}
              <OverviewPositioning
                charts={overviewArea?.charts}
                content={overviewArea?.content}
                onViewInsight={handleViewInsight}
              />

              {/* Show Strategic Recommendations */}
              <OverviewRecommendations
                recommendations={streamResult?.structured_response?.recommendations}
              />

              {/* Show SWOT Analysis */}
              <SWOTAnalysis
                swotData={streamResult?.structured_response?.swot_analysis}
              />

              {/* Show Next Steps */}
              <NextSteps
                steps={streamResult?.structured_response?.next_steps}
              />

              {/* Footer disclaimer */}
              <div className="content-footer">
                This feature is powered by AI. Outputs may contain inaccuracies. Please verify critical information before use.
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    // Handle dynamic area tabs
    const activeTabInfo = tabs.find(tab => tab.id === activeTab);
    const areaName = activeTabInfo?.label;
    const originalKey = activeTabInfo?.originalKey;

    const areas = getAreas();
    if (areaName && originalKey && areas?.[originalKey]) {
      const areaData = areas[originalKey];
      return (
        <div className="tab-content">
          {renderAreaContent(areaName, areaData)}

          {/* Footer disclaimer */}
          <div className="content-footer">
            This feature is powered by AI. Outputs may contain inaccuracies. Please verify critical information before use.
          </div>
        </div>
      );
    }

    // Fallback for tabs without data
    return (
      <div className="tab-content">
        <h3>{tabs.find(tab => tab.id === activeTab)?.label || 'Content'}</h3>
        <p>This section will be populated when analysis data is available.</p>

        {/* Footer disclaimer */}
        <div className="content-footer">
          This feature is powered by AI. Outputs may contain inaccuracies. Please verify critical information before use.
        </div>
      </div>
    );
  };

  return (
    <div className={`content-panel ${hasOverlay ? 'with-overlay' : ''}`}>
      <div className="content-header">
        <div className="project-info">
          <h1 className="project-title">{project.name}</h1>
          {/* <p className="project-subtitle" title={project.description || undefined}>
            {project.description}
          </p> */}
          <p className="project-last-updated">
            Dashboard last synced {getLastSyncTime().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })} | {getLastSyncTime().toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
        <div className="header-actions">
          {!isStreaming && (
            <>
              <button
                className="sync-btn action-btn"
                onClick={handleSyncProject}
                disabled={isSyncing || !onSyncProject}
                title={
                  isSyncing
                    ? 'Syncing in progress...'
                    : isStreaming
                      ? (streamResult?.status === 'pending_approval' && streamResult?.executionPlan
                        ? 'Waiting for approval...'
                        : 'Query in progress...')
                      : 'Re-run initial query to refresh data'
                }
              >
                <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.68945 18.3281C7.59049 18.3281 6.55924 18.1198 5.5957 17.7031C4.63216 17.2917 3.78581 16.7214 3.05664 15.9922C2.32747 15.263 1.75456 14.4167 1.33789 13.4531C0.926432 12.4896 0.720703 11.4583 0.720703 10.3594C0.720703 9.38542 0.884766 8.46354 1.21289 7.59375C1.54102 6.72396 1.99674 5.9401 2.58008 5.24219C3.16862 4.54427 3.85352 3.96354 4.63477 3.5C5.42122 3.03125 6.27018 2.71094 7.18164 2.53906V1.42969C7.18164 1.24219 7.2181 1.10156 7.29102 1.00781C7.36914 0.914062 7.4707 0.869792 7.5957 0.875C7.7207 0.880208 7.85612 0.932292 8.00195 1.03125L10.4863 2.78125C10.6686 2.91146 10.7572 3.0599 10.752 3.22656C10.752 3.39323 10.6634 3.53906 10.4863 3.66406L7.99414 5.41406C7.84831 5.51302 7.71289 5.5651 7.58789 5.57031C7.4681 5.57031 7.36914 5.52344 7.29102 5.42969C7.2181 5.33594 7.18164 5.19792 7.18164 5.01562V3.89062C6.44727 4.0625 5.76758 4.34635 5.14258 4.74219C4.52279 5.13802 3.98112 5.6224 3.51758 6.19531C3.05404 6.76302 2.69206 7.40104 2.43164 8.10938C2.17643 8.81771 2.04883 9.56771 2.04883 10.3594C2.04883 11.276 2.2207 12.1354 2.56445 12.9375C2.9082 13.7396 3.38477 14.4453 3.99414 15.0547C4.60352 15.6641 5.30924 16.1406 6.11133 16.4844C6.91341 16.8281 7.77279 17 8.68945 17C9.60612 17 10.4655 16.8281 11.2676 16.4844C12.0697 16.1406 12.7754 15.6641 13.3848 15.0547C13.9941 14.4453 14.4707 13.7396 14.8145 12.9375C15.1582 12.1354 15.3301 11.276 15.3301 10.3594C15.3301 9.61458 15.2155 8.90885 14.9863 8.24219C14.7572 7.57552 14.4368 6.96615 14.0254 6.41406C13.6139 5.85677 13.1322 5.38021 12.5801 4.98438C12.4186 4.86458 12.3145 4.72135 12.2676 4.55469C12.2259 4.38281 12.2546 4.21354 12.3535 4.04688C12.4525 3.89062 12.5983 3.79427 12.791 3.75781C12.9837 3.72135 13.166 3.77083 13.3379 3.90625C14.0098 4.39062 14.5931 4.96615 15.0879 5.63281C15.5879 6.29427 15.9733 7.02604 16.2441 7.82812C16.5202 8.625 16.6582 9.46875 16.6582 10.3594C16.6582 11.4583 16.4499 12.4896 16.0332 13.4531C15.6217 14.4167 15.0514 15.263 14.3223 15.9922C13.5931 16.7214 12.7467 17.2917 11.7832 17.7031C10.8197 18.1198 9.78841 18.3281 8.68945 18.3281Z" fill="#0D0D0D" />
                </svg>

                {isSyncing ? 'Syncing...' : 'Sync'}
              </button>
              <button
                className="export-btn action-btn"
                onClick={handleExport}
                disabled={isSyncing || !onSyncProject}
              >
                <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.7832 9.72656V12.5625L7.73633 13.8359L8.40039 13.1484L9.10352 12.4297C9.16081 12.3724 9.22591 12.3281 9.29883 12.2969C9.37174 12.2656 9.44727 12.25 9.52539 12.25C9.68164 12.25 9.81185 12.3021 9.91602 12.4062C10.0202 12.5052 10.0723 12.6328 10.0723 12.7891C10.0723 12.8776 10.054 12.9557 10.0176 13.0234C9.98633 13.0911 9.93945 13.1536 9.87695 13.2109L7.62695 15.2812C7.54362 15.3594 7.4681 15.4115 7.40039 15.4375C7.33789 15.4688 7.26758 15.4844 7.18945 15.4844C7.11133 15.4844 7.03841 15.4688 6.9707 15.4375C6.9082 15.4115 6.83529 15.3594 6.75195 15.2812L4.50195 13.2109C4.43945 13.1536 4.38997 13.0911 4.35352 13.0234C4.32227 12.9557 4.30664 12.8776 4.30664 12.7891C4.30664 12.6328 4.35872 12.5052 4.46289 12.4062C4.56706 12.3021 4.69727 12.25 4.85352 12.25C4.92643 12.25 4.99935 12.2656 5.07227 12.2969C5.15039 12.3281 5.2181 12.3724 5.27539 12.4297L5.97852 13.1484L6.64258 13.8359L6.5957 12.5625V9.72656C6.5957 9.57031 6.65299 9.4349 6.76758 9.32031C6.88737 9.20573 7.02799 9.14844 7.18945 9.14844C7.35091 9.14844 7.48893 9.20573 7.60352 9.32031C7.72331 9.4349 7.7832 9.57031 7.7832 9.72656ZM3.04102 17.7344C2.23372 17.7344 1.62695 17.5312 1.2207 17.125C0.819661 16.7188 0.619141 16.1068 0.619141 15.2891V3.4375C0.619141 2.625 0.819661 2.01302 1.2207 1.60156C1.62695 1.1901 2.23372 0.984375 3.04102 0.984375H6.75195C7.04362 0.984375 7.29883 1.00521 7.51758 1.04688C7.74154 1.08333 7.94727 1.15625 8.13477 1.26562C8.32227 1.36979 8.51497 1.52344 8.71289 1.72656L13.0176 6.10938C13.2259 6.32292 13.3822 6.52604 13.4863 6.71875C13.5957 6.90625 13.6686 7.11458 13.7051 7.34375C13.7415 7.57292 13.7598 7.85677 13.7598 8.19531V15.2891C13.7598 16.1016 13.5566 16.7109 13.1504 17.1172C12.7493 17.5286 12.1452 17.7344 11.3379 17.7344H3.04102ZM3.0957 16.4766H11.2754C11.6816 16.4766 11.9863 16.3724 12.1895 16.1641C12.3978 15.9557 12.502 15.6562 12.502 15.2656V8.25H8.04883C7.55924 8.25 7.19206 8.1276 6.94727 7.88281C6.70247 7.63802 6.58008 7.27083 6.58008 6.78125V2.24219H3.10352C2.69727 2.24219 2.38997 2.34896 2.18164 2.5625C1.97852 2.77604 1.87695 3.07552 1.87695 3.46094V15.2656C1.87695 15.6562 1.97852 15.9557 2.18164 16.1641C2.38997 16.3724 2.69466 16.4766 3.0957 16.4766ZM8.18945 7.07031H12.2598L7.75977 2.48438V6.63281C7.75977 6.78906 7.79102 6.90104 7.85352 6.96875C7.92122 7.03646 8.0332 7.07031 8.18945 7.07031Z" fill="white" />
                </svg>

                Export
              </button>
            </>
          )}
        </div>
      </div>

      {!isStreaming && streamResult?.structured_response && (
        <div className="content-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                // Smooth scroll to top of content body
                if (contentBodyRef.current) {
                  contentBodyRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              <div className="tab-label">
                {tab.label}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="content-body" ref={contentBodyRef}>
        {
          (isStreaming && !isFollowUpQuery) || isSyncing ? (
            <div className="is-streaming-content">
              <div className="is-streaming-message">
                {isSyncing && (
                  <div className="sync-progress-container">
                    <div className="sync-progress-bar-wrapper">
                      <div className="sync-progress-bar">
                        <div
                          className="sync-progress-fill"
                          style={{ width: `${syncProgress}%` }}
                        />
                      </div>
                      <span className="sync-progress-percentage">{syncProgress}%</span>
                    </div>
                  </div>
                )}
                <div className="typing-text">
                  {typedText}
                </div>
              </div>
            </div>
          ) :
            ((streamResult?.structured_response || (isStreaming && isFollowUpQuery)) && (renderTabContent()))
        }
      </div>


      <ExportModal
        isOpen={isExportDialogOpen}
        onClose={handleExportDialogClose}
        onExport={handleExportConfirm}
        queryData={streamResult}
        projectName={project.name}
      />

      {selectedReport && (
        <ReportDialogDetails
          isOpen={isReportDialogOpen}
          onClose={handleCloseReportDialog}
          title={selectedReport.title}
          subtitle={selectedReport.subtitle}
          graphData={selectedReport.graphData}
          sourceImages={selectedReport.sources || selectedReport.sourceImages || []}
        />
      )}

      <Toast
        message={toastMessage}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        type={toastType}
      />
    </div>
  );
};

export default WorkspaceContent;